import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import userApi from "../../../services/user";
import roleApi from "../../../services/role";
import CustomDatagrid from "../../../components/datagrid";
import UserFormModal from "../creation";
import {
  getDecryptedCookie,
  encryptAES,
} from "../../../utils";

const useStyles = makeStyles({
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginBottom: "10px",
    marginRight: "15px",
  },
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
});

const UserList = ({ setUserList, userList }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const userId = getDecryptedCookie("uuid");

  const [filters, setFilters] = useState({
    name: "",
    mobilenumber: "",
    roleUuid: "",
    status: "",
  });

  const [openModal, setOpenModal] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortOrder, setSortOrder] = useState([{ field: "id", sort: "desc" }]);
  const [roleList, setRoleList] = useState([]);
  const [userEditData, setUserEditData] = useState({});
  const [mode, setMode] = useState("");
  const [searchparams, setSearchparams] = useState();
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "users"
  );
  const isDisabled = createMenuItem?.access_type === "read_write";

  const fetchUsers = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await userApi.getAllUser(
        paginationModel.page + 1,
        paginationModel.pageSize,
        sortOrder[0]?.field,
        sortOrder[0]?.sort,
        searchparams
      );
      if (response?.data?.status) {
        setUserList(response.data.data.users || []);
        setRowCount(response.data.data.pagination.totalItems || 0);
      } else {
        setUserList([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      dispatch(hideLoader());
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortOrder,
    searchparams,
    dispatch,
  ]);

  const fetchRoles = async () => {
    dispatch(showLoader());
    try {
      const response = await roleApi.getAllRole();
      if (response?.data?.status) {
        setRoleList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      dispatch(hideLoader());
    }
  };
  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [paginationModel, sortOrder]);

  useEffect(() => {
    if (searchparams) {
      fetchUsers();
    }
  }, [searchparams]);

  const userListRows = userList.map((data, index) => ({
    sno_id: index + 1,
    id: index + 1,
    uuid: data?.user?.uuid,
    roleid: data.role?.uuid,
    "user.username": data.user?.username,
    emp_id: data.user?.emp_id,
    email: data.user?.email,
    mobileNumber: data.user?.mobile_number,
    role: data.role?.role,
    status: data?.user?.status,
  }));

  const columns = [
    {
      field: "sno_id",
      headerName: "S.No",
      width: 100,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "user.username",
      headerName: "Name",
      width: 150,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "emp_id",
      headerName: "Emp ID",
      width: 250,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      sortable: false,
    },
    {
      field: "mobileNumber",
      headerName: "Mobile Number",
      width: 200,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      sortable: false,
    },
    {
      field: "role",
      headerName: "Roles",
      width: 150,
      headerAlign: "left",
      align: "center",
      renderCell: (params) => {
        return (
          <span>
            {params.value.charAt(0).toUpperCase() +
              params.value.slice(1).toLowerCase()}
          </span>
        );
      },
      disableColumnMenu: true,
      sortable: false,
    },
    ...(!isDisabled
      ? [
          {
            field: "status",
            headerName: "Status",
            width: 150,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => {
              return (
                <Chip
                  label={params?.row?.status ? "Active" : "In-Active"}
                  color={params?.row?.status ? "success" : "error"}
                  variant="outlined"
                />
              );
            },
          },
        ]
      : []),
    ...(isDisabled
      ? [
          {
            field: "status",
            headerName: "Status",
            width: 150,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => {
              return (
                <Switch
                  checked={params.row.status === true}
                  disabled={
                    params.row.role === "mcisuperadmin" ||
                    userId === params.row.uuid
                  }
                  onChange={() => {
                    updateUserStatus(
                      params.row.uuid,
                      params.row.roleid,
                      params.row.status === true
                    );
                  }}
                  color={params.row.status === true ? "primary" : "default"}
                  inputProps={{ "aria-label": "controlled" }}
                />
              );
            },
          },
          {
            field: "action",
            headerName: "Action",
            sortable: false,
            disableColumnMenu: true,
            width: 150,
            headerAlign: "left",
            align: "center",
            renderCell: (params) =>
              params.row.role !== "mcisuperadmin" &&
              params.row.uuid !== userId && (
                <div style={{ textAlign: "left", width: "100%" }}>
                  <EditIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => editUserById(params.row.uuid)}
                  />
                </div>
              ),
          },
        ]
      : []),
  ];

  const handleCloseModal = () => {
    setOpenModal(false);
    setUserEditData({});
    fetchUsers();
  };

  const editUserById = async (id) => {
    dispatch(showLoader());
    try {
      const response = await userApi.getUserId(id);
      if (response?.data?.status) {
        setUserEditData(response.data.data[0]);
        setOpenModal(true);
        setMode("edit");
      } else {
        console.error("Error fetching user data");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const updateUserStatus = async (userid, roleid, currentStatus) => {
    const data = { status: !currentStatus };
    const encryptedData = encryptAES(data);
    dispatch(showLoader());
    try {
      const response = await userApi.updateUser(userid, roleid, encryptedData);
      if (response?.data?.status) {
        fetchUsers();
      } else {
        dispatch(showSnackbar({ message: response.data.message }));
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSearch = () => {
    const logData = {
      username: filters.name.trim(),
      mobile_number: filters.mobilenumber.trim(),
      role_uuid: filters.roleUuid.trim(),
      status: filters.status.trim(),
    };
    setSearchparams(logData);
  };

  const handleReset = () => {
    setFilters({
      name: "",
      mobilenumber: "",
      roleUuid: "",
      status: "",
    });
    setSearchparams({});
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card elevation={3} sx={{ marginBottom: "15px" }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              label="User Name"
              name="name"
              fullWidth
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Mobile Number"
              name="mobilenumber"
              fullWidth
              value={filters.mobilenumber}
              onChange={(e) =>
                setFilters({ ...filters, mobilenumber: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="roleUuid"
                label="Role"
                value={filters.roleUuid}
                onChange={(e) =>
                  setFilters({ ...filters, roleUuid: e.target.value })
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {roleList.map((role) => (
                  <MenuItem key={role.uuid} value={role.uuid}>
                    {role.role.charAt(0).toUpperCase() +
                      role.role.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                label="Status"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box className={classes.buttonContainer}>
          <Button
            // className={classes.animatedButton}
            variant="outlined"
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            className={classes.animatedButton}
            variant="contained"
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
      </Card>
      <Card elevation={3}>
        <TableContainer component={Paper}>
          {userListRows.length === 0 ? (
            <Typography variant="h6" align="center" sx={{ padding: 2 }}>
              No records matching with your search criteria.
            </Typography>
          ) : (
            <CustomDatagrid
              rows={userListRows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              onSortModelChange={setSortOrder}
              disableSelectionOnClick={true}
              checkboxSelection={false}
              pagination
              pageSizeOptions={[5, 10, 20]}
            />
          )}
        </TableContainer>
      </Card>
      <UserFormModal
        open={openModal}
        onClose={handleCloseModal}
        userEdit={userEditData}
        mode={mode}
        fetchUsers={fetchUsers}
      />
    </Box>
  );
};

export default UserList;
